import React, { useMemo, useEffect } from 'react';
import {
  Flex as ChakraFlex,
  Box as ChakraBox,
  Icon,
  Tooltip as ChakraTooltip,
  useDisclosure,
  Button as ChakraButton,
  type BoxProps,
} from '@chakra-ui/react';
import { Radio } from '@kula-ai/components';
import { ChevronDown, Minus, Plus } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { KPopover } from '@/ui-library/components/organisms/KPopover';

// Chakra UI v2 + TypeScript produces TS2590 ("union type too complex") on
// components with many responsive props. Aliasing as `any` is the standard
// workaround; runtime behaviour is identical.
const Box = ChakraBox as any;
const Flex = ChakraFlex as any;
const Tooltip = ChakraTooltip as any;
const Button = ChakraButton as any;

interface Option {
  key: string;
  label: string;
  value?: number | null;
  stepperMin?: number;
  description?: string;
}

const OPTIONS = {
  ALL: { key: 'all', label: 'All', value: null } as Option,
  ANY_ONE: { key: 'any_one', label: 'Any one', value: 1 } as Option,
  AT_LEAST: { key: 'at_least', label: 'At least', stepperMin: 2 } as Option,
};

interface StepperButtonProps {
  type: 'increment' | 'decrement';
  value: number;
  min: number;
  max: number;
  onChange: (_value: number) => void;
  reviewerCount: number;
}

interface ScorecardThresholdSelectorProps {
  value: number | null | undefined | string;
  onChange: (_value: number | null) => void;
  reviewerCount?: number;
  hasGroupReviewers?: boolean;
}

/** Coerce API / Formik values so `"1"` !== 1 does not break option matching. */
const normalizeThreshold = (
  value: number | null | undefined | string
): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return null;
  return n;
};

const clampValue = (
  value: number | null | undefined | string,
  reviewerCount: number,
  hasGroupReviewers: boolean
): number | null => {
  const n = normalizeThreshold(value);
  if (reviewerCount <= 1 && !hasGroupReviewers) return OPTIONS.ALL.value;
  if (
    reviewerCount === 2 &&
    !hasGroupReviewers &&
    n !== null &&
    n > OPTIONS.ANY_ONE.value
  )
    return OPTIONS.ANY_ONE.value;
  if (!hasGroupReviewers && n !== null && n >= reviewerCount)
    return OPTIONS.ALL.value;
  return n;
};

const getDisplayLabel = (
  value: number | null | undefined | string,
  reviewerCount: number
) => {
  const n = normalizeThreshold(value);
  if (n === null) return OPTIONS.ALL.label;
  if (n === OPTIONS.ANY_ONE.value) return OPTIONS.ANY_ONE.label;
  return `${n} of ${reviewerCount}`;
};

const getSelectedKey = (value: number | null | undefined | string) => {
  const n = normalizeThreshold(value);
  if (n === null) return OPTIONS.ALL.key;
  if (n === OPTIONS.ANY_ONE.value) return OPTIONS.ANY_ONE.key;
  return OPTIONS.AT_LEAST.key;
};

const StepperButton = ({
  type,
  value,
  min,
  max,
  onChange,
  reviewerCount,
}: StepperButtonProps) => {
  const isIncrement = type === 'increment';
  const isDisabled = isIncrement ? value >= max : value <= min;

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    const next = isIncrement
      ? Math.min(value + 1, max)
      : Math.max(value - 1, min);
    onChange(next);
  };

  const icon = (
    <Icon
      as={isIncrement ? Plus : Minus}
      color={isDisabled ? 'secondary-300' : 'secondary-700'}
      pointerEvents="none"
    />
  );

  const buttonStyles: BoxProps = {
    w: '44px',
    h: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bg: 'secondary-25',
    _hover: { bg: isDisabled ? 'secondary-25' : 'secondary-50' },
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    ...(isIncrement
      ? {
          borderLeft: '1px solid',
          borderLeftColor: 'secondary-100',
          borderLeftRadius: '0px',
          borderRightRadius: '8px',
        }
      : {
          borderRight: '1px solid',
          borderRightColor: 'secondary-100',
          borderLeftRadius: '8px',
          borderRightRadius: '0px',
        }),
  };

  if (isIncrement && isDisabled) {
    return (
      <Tooltip
        label={`There are only ${reviewerCount} reviewers added. Select all if you want all reviewers to submit a scorecard.`}
        placement="top"
        hasArrow
      >
        <Box {...buttonStyles} onMouseDown={(e) => e.preventDefault()}>
          {icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      {...buttonStyles}
      onClick={handleClick}
      onMouseDown={(e) => e.preventDefault()}
    >
      {icon}
    </Box>
  );
};

export const ScorecardThresholdSelector = ({
  value,
  onChange,
  reviewerCount = 0,
  hasGroupReviewers = false,
}: ScorecardThresholdSelectorProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const effective = clampValue(value, reviewerCount, hasGroupReviewers);
  const selectedKey = getSelectedKey(effective);
  const isAtLeast = effective !== null && effective > OPTIONS.ANY_ONE.value;
  const stepperMax = Math.max(OPTIONS.AT_LEAST.stepperMin, reviewerCount - 1);
  const defaultAtLeast = Math.max(
    OPTIONS.AT_LEAST.stepperMin,
    reviewerCount - 1
  );
  const stepperValue =
    effective !== null && effective >= OPTIONS.AT_LEAST.stepperMin
      ? Math.min(effective, stepperMax)
      : defaultAtLeast;

  const options = useMemo(() => {
    const allDescription = hasGroupReviewers
      ? 'All must submit'
      : `All ${reviewerCount} must submit`;
    const list = [{ ...OPTIONS.ALL, description: allDescription }];
    if (reviewerCount >= 2 || hasGroupReviewers)
      list.push({
        ...OPTIONS.ANY_ONE,
        description: 'Close after 1st submission',
      });
    if (reviewerCount >= 3 && !hasGroupReviewers)
      list.push({
        ...OPTIONS.AT_LEAST,
        description: 'Set a minimum number',
      });
    return list;
  }, [reviewerCount, hasGroupReviewers]);

  useEffect(() => {
    const n = normalizeThreshold(value);
    if (reviewerCount <= 1 && !hasGroupReviewers && n !== null)
      onChange(OPTIONS.ALL.value);
    else if (
      reviewerCount === 2 &&
      !hasGroupReviewers &&
      n !== null &&
      n > OPTIONS.ANY_ONE.value
    )
      onChange(OPTIONS.ANY_ONE.value);
    else if (hasGroupReviewers && n !== null && n > OPTIONS.ANY_ONE.value)
      onChange(OPTIONS.ALL.value);
    else if (!hasGroupReviewers && n !== null && n >= reviewerCount)
      onChange(OPTIONS.ALL.value);
  }, [reviewerCount, value, onChange, hasGroupReviewers]);

  if (reviewerCount <= 1 && !hasGroupReviewers) return null;

  const handleSelect = (key) => {
    if (key === OPTIONS.ALL.key) {
      onChange(OPTIONS.ALL.value);
      onClose();
    } else if (key === OPTIONS.ANY_ONE.key) {
      onChange(OPTIONS.ANY_ONE.value);
      onClose();
    } else {
      const n = normalizeThreshold(value);
      const cap = Math.max(OPTIONS.AT_LEAST.stepperMin, reviewerCount - 1);
      onChange(
        n !== null && n >= OPTIONS.AT_LEAST.stepperMin && n < reviewerCount
          ? n
          : cap
      );
    }
  };

  return (
    <Flex alignItems="center" gap="8px" flexWrap="wrap" mt="-6px !important">
      <KText fontSize="sm" fontWeight="400" color="secondary-600">
        Require
      </KText>

      <KPopover
        triggerElement={
          <Button
            rightIcon={<ChevronDown size={12} className="kula-icon" />}
            variant="outline"
            size="sm"
            px="8px"
            fontSize="sm"
            fontWeight="400"
            color="secondary-700"
            borderColor="border-color"
            borderRadius="6px"
            _hover={{ bg: 'base-white' }}
            sx={{ height: '24px !important', minHeight: '22px !important' }}
          >
            {getDisplayLabel(effective, reviewerCount)}
          </Button>
        }
        showArrow={false}
        showCloseButton={false}
        popoverActions={{ isOpen, onOpen, onClose }}
        popoverProps={{ placement: 'bottom-start', offset: [0, 4] }}
        popoverContentProps={{
          w: 'max-content',
          minW: 'min-content',
          border: '1px solid',
          borderColor: 'border-color',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: 'drop-shadows-bottom',
          _focus: { outline: 'none' },
        }}
        popoverBodyProps={{ p: 0, minW: '234px' }}
      >
        <Flex
          flexDir="column"
          role="radiogroup"
          aria-label="Scorecard threshold"
        >
          {options.map((opt, idx) => (
            <Box key={opt.key}>
              <Box
                px="12px"
                py="6px"
                cursor="pointer"
                _hover={{ bg: 'secondary-25' }}
                transition="background 0.1s"
                onClick={() => handleSelect(opt.key)}
              >
                <Box
                  sx={{
                    '& input': {
                      appearance: 'none !important',
                      backgroundColor: 'white !important',
                      borderColor:
                        'var(--chakra-colors-secondary-300) !important',
                      boxShadow: 'none !important',
                      outline: 'none !important',
                      WebkitAppearance: 'none !important',
                    },
                    '& input:hover, & input:focus, & input:focus-visible, & input:active, & input:focus-within':
                      {
                        backgroundColor: 'white !important',
                        borderColor:
                          'var(--chakra-colors-secondary-300) !important',
                        boxShadow: 'none !important',
                        outline: 'none !important',
                      },
                  }}
                >
                  <Radio
                    name="scorecard-threshold"
                    value={opt.key}
                    label={opt.label}
                    checked={selectedKey === opt.key}
                    onChange={(e) => handleSelect(e.target.value)}
                    size="sm"
                    className="pointer-events-none"
                    labelClassName="!text-secondary-900 text-sm font-medium"
                  />
                </Box>
                <KText color="secondary-700" mt="1px" pl="20px" fontSize="xs">
                  {opt.description}
                </KText>

                {opt.key === OPTIONS.AT_LEAST.key && isAtLeast && (
                  <Flex
                    mt="3"
                    w="208px"
                    h="36px"
                    border="1px solid"
                    borderColor="border-color"
                    borderRadius="8px"
                    overflow="hidden"
                    bg="white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {OPTIONS.AT_LEAST.stepperMin === stepperMax ? (
                      <Tooltip
                        label={`With ${reviewerCount} reviewers, the minimum count is fixed at ${stepperValue}.`}
                        placement="top"
                        hasArrow
                      >
                        <Flex
                          flex="1"
                          alignItems="center"
                          justifyContent="center"
                          bg="secondary-25"
                          cursor="not-allowed"
                        >
                          <KText
                            fontWeight="500"
                            fontSize="14px"
                            color="secondary-400"
                            userSelect="none"
                          >
                            {stepperValue}
                          </KText>
                        </Flex>
                      </Tooltip>
                    ) : (
                      <>
                        <StepperButton
                          type="decrement"
                          value={stepperValue}
                          min={OPTIONS.AT_LEAST.stepperMin}
                          max={stepperMax}
                          onChange={onChange}
                          reviewerCount={reviewerCount}
                        />
                        <Flex
                          flex="1"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <KText
                            fontWeight="500"
                            fontSize="14px"
                            color="secondary-700"
                            userSelect="none"
                          >
                            {stepperValue}
                          </KText>
                        </Flex>
                        <StepperButton
                          type="increment"
                          value={stepperValue}
                          min={OPTIONS.AT_LEAST.stepperMin}
                          max={stepperMax}
                          onChange={onChange}
                          reviewerCount={reviewerCount}
                        />
                      </>
                    )}
                  </Flex>
                )}
              </Box>

              {idx < options.length - 1 && <Box h="1px" bg="border-color" />}
            </Box>
          ))}
        </Flex>
      </KPopover>

      <KText fontSize="sm" fontWeight="400" color="secondary-600">
        to submit scorecard
      </KText>
    </Flex>
  );
};
